/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { addToast } from "actions/ToastMessageActions";
import { useHistory } from "react-router-dom";
import * as ProjectUtilities from "utilities/ProjectUtilities";
import { ProjectUnauthorizedError } from "utilities/ProjectUtilities";
import { useMounted } from "hooks/useMounted";
import { loadProject } from "actions/ProjectActions";
import { trackEvent, PersonalizationEvent } from "api/CustomerPersonalization";

interface CreateProject {
  createProject: () => Promise<Boolean>;
  isLoading: boolean;
}

export function useCreateProject(): CreateProject {
  const mounted = useMounted();
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const createProject = useCallback(async (): Promise<Boolean> => {
    try {
      setIsLoading(true);
      const newProject = await ProjectUtilities.createProject();
      const newProjectId = newProject?.id;

      if (newProjectId && mounted.current) {
        await dispatch(loadProject(newProject));
        trackEvent(PersonalizationEvent.USER_CREATES_PROJECT);
        history.push(`/projects/${newProjectId}`);
        return true;
      } // TODO: else?
    } catch (error) {
      if (error instanceof ProjectUnauthorizedError) {
        dispatch(addToast({ message: I18n.t("home.studio_unauthorized") }));
        // Don't send error to NR, this is just a user using the wrong account
        history.push("/");
      } else {
        dispatch(addToast({ message: I18n.t("home.new_project_error") }));
        newrelic.noticeError(error);
      }

      if (mounted.current) {
        setIsLoading(false);
      }
    }

    return false;
  }, [setIsLoading, mounted, dispatch, history]);

  return {
    createProject,
    isLoading,
  };
}
